<script setup lang="ts">
// The feature-chat panel: "explain this record", grounded on one insight/anomaly/analysis/alert —
// or, in setup mode, "describe the alert you want" on the Alerts page (KUR-9, ORP-22826).
//
// A SIDE PANEL, opened beside its host rather than inline inside it. v3 rendered a fixed right-hand
// aside next to the host's detail sidebar, which shrank to make room (`FeatureOrpheusPanel.vue` +
// `o-sidebar-modal`'s `right-offset`), and an earlier v4 attempt at rendering this inline inside the
// host drawer produced a cramped column that read as a form field rather than a conversation.
//
// The fixed-positioning apparatus v3 hand-rolled — z-index, pointer-event stoppers, its own resize
// handle — is NOT ported: `shared/ui`'s `Drawer` owns overlay placement, focus return and resizing,
// and raw `Teleport` outside `OverlayPortal` is a lint error anyway. `resize` is forwarded up so the
// host can offset its own drawer live, which is v3's `@width-change` contract.
//
// Message chrome comes from `MessageBubble` — the same bubble the full-page assistant renders, so
// timestamps, copy/edit/retry and follow-up chips match it exactly. That mirrors v3 embedding
// `OrpheusMain` here. `MessageList` is deliberately NOT the reuse point; see `featureChatBubble`.
//
// Setup mode opens without a charged turn: the backend's own suggestions start it, a half-built
// setup is offered back ("continue" / "start over"), and the turns carry the same setup cards and
// credits line as the main chat. A saved draft tells the host, which refreshes what it lists.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { useFeatureChat } from '../../composables/useFeatureChat'
import { onDraftSaved } from '../../composables/useSetupDrafts'
import { useUnavailableChannels } from '../../composables/useUnavailableChannels'
import {
  FEATURE_CHAT_KEYS,
  FEATURE_CHAT_PANEL_WIDTH,
  type FeatureChatMode,
  type FeatureChatTargetRef,
} from '../../models/featureChat.types'
import { toChatMessages, trailingFailure } from '../../service/featureChatBubble'
import { useOrpheusConsentStore } from '../../stores/orpheusConsentStore'
import OrpheusConsentCompact from '../consent/OrpheusConsentCompact.vue'
import MessageBubble from '../MessageBubble.vue'
import OrpheusComposer from '../OrpheusComposer.vue'

// old's panel header is the Orpheus WORDMARK, not a mark plus a label
// (`FeatureOrpheusPanel.vue`: `orpheus_header.png`) — the same asset the shared logo set already
// ships, so it is used from there rather than duplicated into this feature.
import { useAccount, useWorkspace } from '@/features/account'
import { FeatureDepletedBanner } from '@/features/credits'
import wordmark from '@/shared/assets/logos/orpheus_header.png'
import logo from '@/shared/assets/logos/orpheus_logo.png'
import { useI18n } from '@/shared/i18n'
import { Alert, Button, Icon } from '@/shared/ui'
import { Drawer } from '@/shared/ui/drawer'

/** v3 `FeatureOrpheusPanel`'s MIN_PANEL_WIDTH; the default is shared with the hosts that offset by it. */
const MIN_WIDTH = 400

const props = withDefaults(
  defineProps<{
    featureKey: string
    targetRef: FeatureChatTargetRef | null
    /** Identifies the record within the feature; changing it starts a new conversation. */
    focusKey: number | string | null
    /** The opening question asked automatically when the panel opens on a new record. */
    question: string
    contextLabel?: string
    /** `setup` opens empty and waits for the user (no opening question, no target). */
    mode?: FeatureChatMode
    /** Setup mode's line under the greeting. */
    introText?: string
    /** What a form already held, carried in as the first message's draft — a new setup. */
    initialInput?: string
  }>(),
  { contextLabel: '', mode: 'talk', introText: '', initialInput: '' },
)
const emit = defineEmits<{
  close: []
  resize: [width: number]
  /** Setup mode: "Fill in the form instead". */
  'open-form': []
  /** Setup mode: a draft from this panel was saved; the result is the write's. */
  saved: [result: Record<string, unknown> | null]
}>()

const { t } = useI18n()
const chat = useFeatureChat()
const consent = useOrpheusConsentStore()
const { accountId } = useAccount()
const { workspaceId } = useWorkspace()
const open = ref(true)
const copiedLocalId = ref<string | null>(null)
const draft = ref(props.initialInput)

const isSetup = computed(() => props.mode === 'setup')
const groundedOn = computed(() => props.contextLabel?.trim() || props.featureKey)
const bubbles = computed(() => toChatMessages(chat.messages.value))
const failure = computed(() => trailingFailure(chat.messages.value))
const latestLocalId = computed(
  () => bubbles.value.filter((message) => message.streamState === 'completed').at(-1)?.localId,
)
const latestCardLocalId = computed(
  () => bubbles.value.filter((message) => message.setupCard).at(-1)?.localId,
)
// Only the alert setup asks for channels; elsewhere nothing is greyed out.
const channels = useUnavailableChannels()
const unavailableOptions = computed(() =>
  props.featureKey === FEATURE_CHAT_KEYS.ALERT_CREATE ? { channel_type: channels.value } : {},
)
/** Only the server may name a wait; a hardcoded "a few seconds" would be a number we invented. */
const rateLimitText = computed(() => {
  const seconds = failure.value?.error?.retryAfterSeconds
  return seconds === null || seconds === undefined
    ? t('orpheus.featureChat.error.rateLimited')
    : t('orpheus.featureChat.error.rateLimitedIn', { seconds })
})

async function copy(content: string, localId: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(content)
    copiedLocalId.value = localId
  } catch {
    // A denied clipboard permission is not worth a surface; the text is on screen either way.
  }
}

function send(content: string): void {
  void chat.ask(content)
}

/** Re-ask the opening question — the failed turn stays in the transcript above it. */
function retry(): void {
  if (props.question) void chat.ask(props.question)
}

// The host mounts this only while the panel is open (`v-if`), so opening the conversation belongs to
// mount, and a record change while open starts a fresh one. Keeping the panel mounted-but-hidden
// would run the composable — and its workspace/credit reads — for every detail view nobody asked a
// question in.
watch(
  () => props.focusKey,
  () => {
    if (isSetup.value) {
      void chat.openSetup(props.featureKey, Boolean(props.initialInput.trim()))
      return
    }
    if (props.targetRef === null) return
    void chat.open({ featureKey: props.featureKey, targetRef: props.targetRef }, props.question)
  },
  { immediate: true },
)

const stopDraftSaved = onDraftSaved((draftRef, result) => {
  if (chat.messages.value.some((message) => message.setupCard?.draftRef === draftRef)) {
    emit('saved', result)
  }
})
onBeforeUnmount(stopDraftSaved)
onMounted(() => void consent.fetchConsent(accountId.value, workspaceId.value))

// The drawer owns its own close affordance and Escape handling; both mean the same thing here.
watch(open, (isOpen) => {
  if (!isOpen) emit('close')
})
</script>

<template>
  <Drawer
    v-model="open"
    class="feature-chat"
    placement="right"
    resizable
    :width="FEATURE_CHAT_PANEL_WIDTH"
    :min-width="MIN_WIDTH"
    :close-on-outside="false"
    :close-label="t('orpheus.featureChat.close')"
    data-testid="feature-chat-panel"
    @resize="emit('resize', $event)"
  >
    <template #header>
      <img
        class="feature-chat__wordmark block w-30 h-auto"
        :src="wordmark"
        :alt="t('orpheus.featureChat.title')"
        width="120"
      />
    </template>

    <!-- old's "Grounded on <name>" band: a tinted strip spanning the panel edge to edge with a
         bottom rule and no radius, flush under the header (`FeatureOrpheusPanel.vue:57`). The
         negative inline margin cancels the drawer body's own padding — this is chrome, not body
         copy. One continuous line: the lock and the sentence flow inline, and `i18n-t` keeps the
         sentence whole in the catalogue while only the record's name is emphasised. old prefixed
         it with a lock EMOJI; the registry's lock icon is the same glyph in this codebase's terms. -->
    <div
      class="feature-chat__grounded mt-[calc(-1_*_var(--ui-space-md))] mx-[calc(-1_*_var(--ui-space-lg))] mb-md flex flex-wrap items-center justify-between gap-2 py-sm px-lg border-b border-b-orpheus-muted bg-orpheus-soft text-orpheus text-[0.875rem]"
    >
      <span>
        <Icon name="lock" :size="12" class="mr-2xs" aria-hidden="true" />
        <i18n-t keypath="orpheus.featureChat.groundedOn" tag="span" scope="global">
          <template #context
            ><strong>{{ groundedOn }}</strong></template
          >
        </i18n-t>
      </span>
      <button
        v-if="isSetup"
        type="button"
        class="cursor-pointer border-0 bg-transparent p-0 text-sm font-medium text-info hover:underline"
        data-testid="feature-chat-open-form"
        @click="emit('open-form')"
      >
        {{ t('orpheus.featureChat.setup.openForm') }}
      </button>
    </div>

    <OrpheusConsentCompact v-if="consent.isOff" />

    <template v-else>
      <div
        v-if="chat.pendingSession.value"
        class="feature-chat__resume mx-[calc(-1_*_var(--ui-space-lg))] -mt-md mb-md border-b border-border px-lg py-md text-sm text-fg"
        data-testid="feature-chat-resume"
      >
        <p class="m-0">{{ t('orpheus.featureChat.setup.resume') }}</p>
        <div class="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            data-testid="feature-chat-resume-continue"
            @click="chat.continueSession"
          >
            {{ t('orpheus.featureChat.setup.continue') }}
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid="feature-chat-resume-restart"
            @click="chat.startOver"
          >
            {{ t('orpheus.featureChat.setup.startOver') }}
          </Button>
        </div>
      </div>

      <!-- Setup mode before anything is asked: v3's greeting, the intro and the backend's own
           suggested openings, above the composer (`OrpheusMain` with no thread). -->
      <section
        v-if="isSetup && bubbles.length === 0"
        class="feature-chat__intro flex w-full flex-col items-center gap-lg py-lg text-center"
        data-testid="feature-chat-setup-intro"
      >
        <div class="flex w-full flex-col items-center gap-1">
          <p class="m-0 flex items-center text-2xl font-semibold text-fg">
            <img :src="logo" alt="" class="mr-2 w-7 object-contain" width="28" height="28" />
            {{ t('orpheus.intro.greeting') }}
          </p>
          <p class="m-0 text-2xl font-semibold text-fg">{{ t('orpheus.intro.subtitle') }}</p>
          <p
            v-if="introText"
            class="mt-2 mb-0 w-full max-w-[24rem] text-center text-base font-light text-muted"
          >
            {{ introText }}
          </p>
        </div>
        <div
          v-if="chat.suggestions.value.length"
          class="w-full"
          data-testid="feature-chat-setup-suggestions"
        >
          <p class="mt-0 mb-3 text-xs font-semibold uppercase tracking-widest text-subtle">
            {{ t('orpheus.suggestions.label') }}
          </p>
          <div class="flex flex-wrap justify-center gap-3">
            <button
              v-for="prompt in chat.suggestions.value"
              :key="prompt"
              type="button"
              class="cursor-pointer rounded-full border border-border bg-surface px-4 py-2 text-sm font-medium text-fg shadow-sm transition-all duration-200 hover:border-orpheus-brand/50 hover:text-orpheus-brand hover:shadow-orpheus-composer"
              @click="send(prompt)"
            >
              {{ prompt }}
            </button>
          </div>
        </div>
      </section>

      <!-- old separates turns by `mb-8` — 2rem (`OrpheusChatArea.vue:21`). -->
      <div class="feature-chat__turns grid gap-xl min-w-0">
        <MessageBubble
          v-for="message in bubbles"
          :key="message.localId"
          :message="message"
          compact
          :copied="copiedLocalId === message.localId"
          :busy="chat.busy.value"
          :latest="latestLocalId === message.localId"
          :latest-card="latestCardLocalId === message.localId"
          :unavailable-options="unavailableOptions"
          @copy="copy(message.content, message.localId)"
          @retry="retry"
          @send="send($event.text)"
        />

        <FeatureDepletedBanner
          v-if="failure?.error?.kind === 'creditDepleted'"
          :feature="t('orpheus.title')"
        />
        <Alert v-else-if="failure?.error?.kind === 'rateLimited'" tone="warning">
          {{ rateLimitText }}
        </Alert>
        <Alert v-else-if="failure" tone="danger">
          <span class="feature-chat__failure flex items-center justify-between gap-sm">
            <span>{{ failure.error?.message || t('orpheus.featureChat.error.generic') }}</span>
            <Button v-if="question" variant="outline" size="xs" @click="retry">
              {{ t('orpheus.featureChat.error.retry') }}
            </Button>
          </span>
        </Alert>
      </div>
    </template>

    <template #footer>
      <!-- The composer owns its own draft, placeholder and disclaimer — the same control the
           full-page assistant uses, which is what v3 got by embedding `OrpheusMain` here. The
           bottom margin lifts it off the panel edge, as old's did; it sits on the composer rather
           than on `.ui-drawer__footer`, which 34 other surfaces share. -->
      <OrpheusComposer
        v-if="!consent.isOff"
        v-model="draft"
        class="mb-xl"
        :streaming="chat.busy.value"
        @send="send"
      />
    </template>
  </Drawer>
</template>
